import { useMemo, useState } from 'react';
import { Check, Copy, Info, Minus, Plus, Shield, Trash2, UserCog, Users, X } from 'lucide-react';
import type { CustomRole, Permission, SessionUser, UserGroup } from '@itr/shared';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Badge, Drawer, EmptyState, ErrorState, SkeletonTable, useFetch, useToast } from '../components/ui';

interface PermissionCatalogue {
  permissions: Permission[];
  groups: Array<{ label: string; permissions: Permission[] }>;
  labels: Partial<Record<Permission, string>>;
}

interface EffectiveResponse {
  user: { id: string; name: string; email: string; role: string };
  effective: { permissions: Permission[]; sources: Record<string, string[]>; denied: Permission[]; groupNames: string[] };
  overrides: Array<{ permission: Permission; effect: 'allow' | 'deny'; reason: string | null }>;
  groups: Array<{ id: string; name: string }>;
}

export function AccessPage() {
  const { can } = useAuth();
  const [tab, setTab] = useState<'roles' | 'groups' | 'users'>('roles');

  if (!can('user:read')) {
    return <EmptyState icon={<Shield size={20} />} title="Not available" text="Your role cannot view access control." />;
  }

  return (
    <div className="content-narrow" style={{ maxWidth: 1100 }}>
      <div className="page-header">
        <div className="grow">
          <h1 className="page-title">Access control</h1>
          <div className="page-subtitle">
            Roles grant permissions, groups apply roles to people, and a per-user grant or revocation overrides both.
          </div>
        </div>
      </div>

      <div className="row" style={{ gap: 4, marginBottom: 16 }}>
        {(
          [
            ['roles', 'Roles', Shield],
            ['groups', 'Groups', Users],
            ['users', 'Per-user', UserCog],
          ] as const
        ).map(([id, label, Icon]) => (
          <button key={id} className={`btn btn-sm${tab === id ? ' btn-primary' : ''}`} onClick={() => setTab(id)}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === 'roles' && <RolesTab />}
      {tab === 'groups' && <GroupsTab />}
      {tab === 'users' && <UserPermissionsTab />}
    </div>
  );
}

/* ================================================================== */
/* Roles                                                              */
/* ================================================================== */

function RolesTab() {
  const { can } = useAuth();
  const toast = useToast();
  const [editing, setEditing] = useState<CustomRole | null>(null);
  const [creating, setCreating] = useState(false);

  const roles = useFetch(() => api.get<CustomRole[]>('/api/access/roles'), []);
  const catalogue = useFetch(() => api.get<PermissionCatalogue>('/api/access/permissions'), []);

  const duplicate = async (role: CustomRole) => {
    const name = prompt(`Name for the copy of "${role.name}"?`, `${role.name}-copy`);
    if (!name) return;
    await api.post(`/api/access/roles/${role.id}/duplicate`, { name });
    roles.reload();
    toast('Role duplicated — it is now editable', 'good');
  };

  const remove = async (role: CustomRole) => {
    if (!confirm(`Delete the role "${role.name}"?`)) return;
    try {
      await api.delete(`/api/access/roles/${role.id}`);
      roles.reload();
      toast('Role deleted', 'good');
    } catch (err) {
      toast('Could not delete the role', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  if (roles.loading) return <SkeletonTable rows={6} cols={3} />;
  if (roles.error) return <ErrorState error={roles.error} onRetry={roles.reload} />;

  return (
    <>
      {can('user:manage') && (
        <div className="row" style={{ marginBottom: 12 }}>
          <span className="grow" />
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            <Plus size={15} /> New role
          </button>
        </div>
      )}

      <div className="grid-auto">
        {(roles.data ?? []).map((role) => (
          <div className="card" key={role.id}>
            <div className="card-header">
              <Shield size={15} style={{ color: role.builtin ? 'var(--itr-text-subtle)' : 'var(--itr-primary)' }} />
              <div className="grow" style={{ minWidth: 0 }}>
                <div className="card-title truncate" style={{ textTransform: 'capitalize' }}>
                  {role.name}
                </div>
                <div className="card-subtitle">{role.permissions.length} permissions</div>
              </div>
              {role.builtin && <Badge>built-in</Badge>}
            </div>

            <div className="card-body">
              <p className="subtle" style={{ fontSize: 12.5, minHeight: 34, lineHeight: 1.5 }}>
                {role.description || '—'}
              </p>

              <div className="row" style={{ gap: 4, flexWrap: 'wrap', marginTop: 8 }}>
                {role.permissions.slice(0, 5).map((p) => (
                  <span key={p} className="badge" style={{ height: 17, fontSize: 10 }}>
                    {p}
                  </span>
                ))}
                {role.permissions.length > 5 && (
                  <span className="badge" style={{ height: 17, fontSize: 10 }}>
                    +{role.permissions.length - 5}
                  </span>
                )}
              </div>

              {can('user:manage') && (
                <div className="row" style={{ marginTop: 14, gap: 6 }}>
                  {/* A built-in is re-seeded from code on every boot, so editing
                      it would silently revert — duplicating is the way in. */}
                  <button className="btn btn-sm" onClick={() => (role.builtin ? duplicate(role) : setEditing(role))}>
                    {role.builtin ? (
                      <>
                        <Copy size={13} /> Duplicate
                      </>
                    ) : (
                      'Edit'
                    )}
                  </button>
                  <span className="grow" />
                  {!role.builtin && (
                    <button className="btn btn-sm btn-icon" onClick={() => remove(role)} aria-label="Delete role">
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Drawer
        open={creating || Boolean(editing)}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        title={editing ? `Edit ${editing.name}` : 'New role'}
      >
        <RoleForm
          role={editing}
          catalogue={catalogue.data}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            roles.reload();
            toast('Role saved', 'good');
          }}
        />
      </Drawer>
    </>
  );
}

function RoleForm({
  role,
  catalogue,
  onSaved,
}: {
  role: CustomRole | null;
  catalogue: PermissionCatalogue | undefined;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(role?.name ?? '');
  const [description, setDescription] = useState(role?.description ?? '');
  const [selected, setSelected] = useState<Set<Permission>>(new Set(role?.permissions ?? []));
  const [busy, setBusy] = useState(false);

  const toggle = (permission: Permission) =>
    setSelected((current) => {
      const next = new Set(current);
      next.has(permission) ? next.delete(permission) : next.add(permission);
      return next;
    });

  const toggleGroup = (permissions: Permission[]) =>
    setSelected((current) => {
      const next = new Set(current);
      const allOn = permissions.every((p) => next.has(p));
      for (const p of permissions) (allOn ? next.delete(p) : next.add(p));
      return next;
    });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const payload = { name: name.trim(), description, permissions: [...selected], customerId: null };
    try {
      if (role) await api.patch(`/api/access/roles/${role.id}`, payload);
      else await api.post('/api/access/roles', payload);
      onSaved();
    } catch (err) {
      toast('Save failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="field">
        <label className="field-label">Name</label>
        <input className="input" required autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="noc-operator" />
      </div>

      <div className="field">
        <label className="field-label">Description</label>
        <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What this role is for" />
      </div>

      <div className="field-label" style={{ marginBottom: 8 }}>
        Permissions — {selected.size} selected
      </div>

      {(catalogue?.groups ?? []).map((group) => {
        const allOn = group.permissions.every((p) => selected.has(p));
        return (
          <section key={group.label} style={{ marginBottom: 16 }}>
            <div className="row" style={{ marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 620 }}>{group.label}</span>
              <span className="grow" />
              <button type="button" className="btn btn-sm btn-ghost" onClick={() => toggleGroup(group.permissions)}>
                {allOn ? 'None' : 'All'}
              </button>
            </div>
            <div className="col" style={{ gap: 4 }}>
              {group.permissions.map((permission) => (
                <label
                  key={permission}
                  className="row"
                  style={{
                    gap: 9,
                    padding: '7px 9px',
                    borderRadius: 'var(--itr-radius-sm)',
                    background: selected.has(permission) ? 'color-mix(in srgb, var(--itr-primary) 9%, transparent)' : 'var(--itr-surface-sunken)',
                    cursor: 'pointer',
                  }}
                >
                  <input type="checkbox" checked={selected.has(permission)} onChange={() => toggle(permission)} />
                  <span style={{ minWidth: 0 }}>
                    <span className="mono" style={{ fontSize: 11.5, display: 'block' }}>
                      {permission}
                    </span>
                    {/* The label is what makes a 29-permission list decidable —
                        an admin should not have to guess what a scope does. */}
                    <span className="subtle" style={{ fontSize: 11.5 }}>
                      {catalogue?.labels[permission] ?? ''}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </section>
        );
      })}

      <button className="btn btn-primary" type="submit" disabled={busy || !name.trim()} style={{ width: '100%' }}>
        {busy ? 'Saving…' : role ? 'Save role' : 'Create role'}
      </button>
    </form>
  );
}

/* ================================================================== */
/* Groups                                                             */
/* ================================================================== */

function GroupsTab() {
  const { can } = useAuth();
  const toast = useToast();
  const [editing, setEditing] = useState<UserGroup | null>(null);
  const [creating, setCreating] = useState(false);

  const groups = useFetch(() => api.get<UserGroup[]>('/api/access/groups'), []);
  const roles = useFetch(() => api.get<CustomRole[]>('/api/access/roles'), []);
  const users = useFetch(() => api.get<SessionUser[]>('/api/users'), []);

  const roleName = (id: string) => roles.data?.find((r) => r.id === id)?.name ?? id;

  if (groups.loading) return <SkeletonTable rows={4} cols={3} />;

  return (
    <>
      {can('user:manage') && (
        <div className="row" style={{ marginBottom: 12 }}>
          <span className="grow" />
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            <Plus size={15} /> New group
          </button>
        </div>
      )}

      {(groups.data ?? []).length === 0 ? (
        <EmptyState
          icon={<Users size={20} />}
          title="No groups yet"
          text="A group applies one or more roles to a set of people — useful when a team needs the same access without giving each person a bespoke role."
        />
      ) : (
        <div className="card">
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Group</th>
                  <th>Roles</th>
                  <th data-align="right" style={{ width: 100 }}>
                    Members
                  </th>
                  <th style={{ width: 90 }} />
                </tr>
              </thead>
              <tbody>
                {(groups.data ?? []).map((group) => (
                  <tr key={group.id}>
                    <td>
                      <div style={{ fontWeight: 550 }}>{group.name}</div>
                      {group.description && (
                        <div className="subtle" style={{ fontSize: 12 }}>
                          {group.description}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="row" style={{ gap: 4, flexWrap: 'wrap' }}>
                        {group.roleIds.map((id) => (
                          <Badge key={id}>{roleName(id)}</Badge>
                        ))}
                        {group.roleIds.length === 0 && <span className="subtle">—</span>}
                      </div>
                    </td>
                    <td data-align="right" className="nums">
                      {group.memberIds.length}
                    </td>
                    <td>
                      {can('user:manage') && (
                        <div className="row" style={{ gap: 4 }}>
                          <button className="btn btn-sm" onClick={() => setEditing(group)}>
                            Edit
                          </button>
                          <button
                            className="btn btn-sm btn-icon"
                            onClick={async () => {
                              if (!confirm(`Delete the group "${group.name}"?`)) return;
                              await api.delete(`/api/access/groups/${group.id}`);
                              groups.reload();
                              toast('Group deleted', 'good');
                            }}
                            aria-label="Delete group"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Drawer
        open={creating || Boolean(editing)}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
        title={editing ? `Edit ${editing.name}` : 'New group'}
      >
        <GroupForm
          group={editing}
          roles={roles.data ?? []}
          users={users.data ?? []}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            groups.reload();
            toast('Group saved', 'good');
          }}
        />
      </Drawer>
    </>
  );
}

function GroupForm({
  group,
  roles,
  users,
  onSaved,
}: {
  group: UserGroup | null;
  roles: CustomRole[];
  users: SessionUser[];
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(group?.name ?? '');
  const [description, setDescription] = useState(group?.description ?? '');
  const [roleIds, setRoleIds] = useState<Set<string>>(new Set(group?.roleIds ?? []));
  const [memberIds, setMemberIds] = useState<Set<string>>(new Set(group?.memberIds ?? []));
  const [busy, setBusy] = useState(false);

  const toggle = (set: Set<string>, setter: (s: Set<string>) => void) => (id: string) => {
    const next = new Set(set);
    next.has(id) ? next.delete(id) : next.add(id);
    setter(next);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    const payload = {
      name: name.trim(),
      description,
      customerId: null,
      roleIds: [...roleIds],
      memberIds: [...memberIds],
    };
    try {
      if (group) await api.put(`/api/access/groups/${group.id}`, payload);
      else await api.post('/api/access/groups', payload);
      onSaved();
    } catch (err) {
      toast('Save failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit}>
      <div className="field">
        <label className="field-label">Name</label>
        <input className="input" required autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Network team" />
      </div>
      <div className="field">
        <label className="field-label">Description</label>
        <input className="input" value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>

      <div className="field-label" style={{ marginBottom: 7 }}>
        Roles this group grants
      </div>
      <div className="col" style={{ gap: 4, marginBottom: 18 }}>
        {roles.map((role) => (
          <label key={role.id} className="row" style={{ gap: 9, padding: '7px 9px', borderRadius: 'var(--itr-radius-sm)', background: 'var(--itr-surface-sunken)', cursor: 'pointer' }}>
            <input type="checkbox" checked={roleIds.has(role.id)} onChange={() => toggle(roleIds, setRoleIds)(role.id)} />
            <span style={{ textTransform: 'capitalize', fontSize: 13 }}>{role.name}</span>
            <span className="grow" />
            <span className="subtle" style={{ fontSize: 11.5 }}>
              {role.permissions.length} perms
            </span>
          </label>
        ))}
      </div>

      <div className="field-label" style={{ marginBottom: 7 }}>
        Members — {memberIds.size} selected
      </div>
      <div className="col" style={{ gap: 4, marginBottom: 18, maxHeight: 260, overflowY: 'auto' }}>
        {users.map((user) => (
          <label key={user.id} className="row" style={{ gap: 9, padding: '7px 9px', borderRadius: 'var(--itr-radius-sm)', background: 'var(--itr-surface-sunken)', cursor: 'pointer' }}>
            <input type="checkbox" checked={memberIds.has(user.id)} onChange={() => toggle(memberIds, setMemberIds)(user.id)} />
            <span style={{ fontSize: 13 }}>{user.name}</span>
            <span className="grow" />
            <span className="subtle" style={{ fontSize: 11.5 }}>
              {user.email}
            </span>
          </label>
        ))}
      </div>

      <button className="btn btn-primary" type="submit" disabled={busy || !name.trim()} style={{ width: '100%' }}>
        {busy ? 'Saving…' : 'Save group'}
      </button>
    </form>
  );
}

/* ================================================================== */
/* Per-user permissions                                               */
/* ================================================================== */

function UserPermissionsTab() {
  const { can, user: me } = useAuth();
  const toast = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const users = useFetch(() => api.get<SessionUser[]>('/api/users'), []);
  const catalogue = useFetch(() => api.get<PermissionCatalogue>('/api/access/permissions'), []);
  const detail = useFetch(
    () => api.get<EffectiveResponse>(`/api/access/users/${selectedId}/permissions`),
    [selectedId],
    { enabled: Boolean(selectedId) },
  );

  const overrideMap = useMemo(
    () => new Map((detail.data?.overrides ?? []).map((o) => [o.permission, o.effect])),
    [detail.data],
  );

  const setOverride = async (permission: Permission, effect: 'allow' | 'deny' | null) => {
    try {
      await api.put(`/api/access/users/${selectedId}/permissions`, { permission, effect });
      detail.reload();
      toast(effect ? `${permission} ${effect === 'allow' ? 'granted' : 'revoked'}` : 'Override cleared', 'good');
    } catch (err) {
      toast('Could not change the permission', 'bad', err instanceof Error ? err.message : undefined);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) 1fr', gap: 14, alignItems: 'start' }}>
      <div className="card">
        <div className="card-header">
          <span className="card-title">Users</span>
        </div>
        <div style={{ maxHeight: 560, overflowY: 'auto', padding: '4px 0' }}>
          {(users.data ?? []).map((user) => (
            <button
              key={user.id}
              onClick={() => setSelectedId(user.id)}
              className="row"
              style={{
                width: '100%',
                gap: 8,
                padding: '9px 14px',
                background: selectedId === user.id ? 'var(--itr-surface-sunken)' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                color: 'inherit',
              }}
            >
              <span className="grow" style={{ minWidth: 0 }}>
                <span className="truncate" style={{ display: 'block', fontSize: 13, fontWeight: 540 }}>
                  {user.name}
                </span>
                <span className="truncate subtle" style={{ display: 'block', fontSize: 11.5 }}>
                  {user.email}
                </span>
              </span>
              <Badge>{user.role}</Badge>
            </button>
          ))}
        </div>
      </div>

      {!selectedId ? (
        <EmptyState icon={<UserCog size={20} />} title="Select a user" text="Grant or revoke a single capability without creating a whole new role for them." />
      ) : detail.loading ? (
        <SkeletonTable rows={8} cols={2} />
      ) : (
        <div className="card">
          <div className="card-header">
            <span className="card-title">{detail.data?.user.name}</span>
            <span className="grow" />
            <Badge>{detail.data?.user.role}</Badge>
            {(detail.data?.effective.groupNames ?? []).map((g) => (
              <Badge key={g} intent="info">
                {g}
              </Badge>
            ))}
          </div>

          <div className="card-body">
            <div
              className="row"
              style={{
                gap: 8,
                padding: '9px 11px',
                marginBottom: 14,
                borderRadius: 'var(--itr-radius-sm)',
                background: 'var(--itr-surface-sunken)',
                fontSize: 12.5,
              }}
            >
              <Info size={14} style={{ color: 'var(--itr-text-subtle)', flexShrink: 0 }} />
              <span className="subtle">
                A tick means the role or a group already grants it. Use <strong>+</strong> to grant this person only, and{' '}
                <strong>−</strong> to revoke it even when a role or group would allow it — a revocation always wins.
              </span>
            </div>

            {(catalogue.data?.groups ?? []).map((group) => (
              <section key={group.label} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 620, marginBottom: 6 }}>{group.label}</div>
                <div className="col" style={{ gap: 3 }}>
                  {group.permissions.map((permission) => {
                    const override = overrideMap.get(permission);
                    const granted = detail.data?.effective.permissions.includes(permission) ?? false;
                    const sources = detail.data?.effective.sources[permission] ?? [];

                    return (
                      <div
                        key={permission}
                        className="row"
                        style={{
                          gap: 9,
                          padding: '6px 9px',
                          borderRadius: 'var(--itr-radius-sm)',
                          background:
                            override === 'deny'
                              ? 'color-mix(in srgb, var(--itr-critical) 10%, transparent)'
                              : override === 'allow'
                                ? 'color-mix(in srgb, var(--itr-success) 10%, transparent)'
                                : 'transparent',
                        }}
                      >
                        <span style={{ width: 16, flexShrink: 0 }}>
                          {granted ? (
                            <Check size={14} style={{ color: 'var(--itr-success)' }} />
                          ) : (
                            <X size={14} style={{ color: 'var(--itr-text-subtle)' }} />
                          )}
                        </span>

                        <span className="grow" style={{ minWidth: 0 }}>
                          <span className="mono" style={{ fontSize: 11.5, display: 'block' }}>
                            {permission}
                          </span>
                          {/* Provenance: answers "why can they do this" without
                              making an admin reverse-engineer the group graph. */}
                          <span className="subtle" style={{ fontSize: 11 }}>
                            {override === 'deny'
                              ? 'revoked for this user'
                              : sources.length
                                ? `via ${sources.join(', ')}`
                                : catalogue.data?.labels[permission] ?? ''}
                          </span>
                        </span>

                        {can('user:manage') && (
                          <span className="row" style={{ gap: 3 }}>
                            <button
                              className="btn btn-sm btn-icon"
                              title="Grant to this user"
                              aria-label={`Grant ${permission}`}
                              disabled={override === 'allow'}
                              onClick={() => setOverride(permission, 'allow')}
                            >
                              <Plus size={12} />
                            </button>
                            <button
                              className="btn btn-sm btn-icon"
                              title="Revoke for this user"
                              aria-label={`Revoke ${permission}`}
                              disabled={override === 'deny' || (me?.id === selectedId && permission === 'user:manage')}
                              onClick={() => setOverride(permission, 'deny')}
                            >
                              <Minus size={12} />
                            </button>
                            {override && (
                              <button
                                className="btn btn-sm btn-icon"
                                title="Clear the override"
                                aria-label={`Clear ${permission}`}
                                onClick={() => setOverride(permission, null)}
                              >
                                <X size={12} />
                              </button>
                            )}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
